// deviceOfflineCache — the roster an app device holds when there is no signal.
// Spec: docs/specs/device-and-worker-sign-in.md §5, D2/D7. Phase 4b.
//
// This module is the reason a picking crew in an orchard with no coverage can
// still sign in, and it is also the largest thing a stolen tablet gives away.
// Both facts shape every decision below.
//
// WHAT IS CACHED, and what each costs if the device is stolen:
//   worker_number, display_name, photo_path  — a printed crew list already
//     leaks these; the exposure is real but bounded and re-issuable.
//   badge_code_hash                          — tenant-salted; a thief must
//     brute-force per tenant, and a forged badge still only yields capture
//     rows that the drain re-verifies.
//   face templates                           — ⚠️ NOT RE-ISSUABLE. A worker can
//     be given a new badge, PIN or number. Nobody can be given a new face.
//
// ⚠️⚠️ THREE THINGS BOUND THE DAMAGE, AND ALL THREE ARE LOAD-BEARING:
//
//   1. ENCRYPTED AT REST with AES-GCM, key derived by HKDF from the per-device
//      secret. The secret lives in a different localStorage slot from the
//      bearer token, so lifting one does not automatically yield the other.
//      No secret ⇒ NO CACHE. There is deliberately no fallback key: a fixed one
//      would encrypt every tenant's roster identically, which is worse than
//      caching nothing.
//   2. IT EXPIRES, at the profile's offline window (D7, 24h default). Past that
//      the cache is refused, not merely stale — a revoked face enrolment must
//      stop working on a device that never comes back to the network, and the
//      TTL is the only thing that achieves it.
//   3. IT IS WIPEABLE. clearOfflineCache() is called by the WIPE command and by
//      unpairDevice().
//
// ⚠️ `navigator.onLine` IS A HINT, NOT A FACT. Anchor's gate kiosk sat
// "syncing" for 20 days behind a connected-but-dead uplink (2026-07-28). Every
// freshness decision here keys on a PROVEN-CONTACT stamp written only after a
// real server response — never on the browser flag.

import { getDeviceSecret } from '@/lib/deviceSession'
import { deviceSecretBytes } from '@/lib/deviceSecret'
// ⚠️ For the IV and the ciphertext ONLY. The SECRET is hex and goes through
// deviceSecretBytes() — decoding it as base64 was the bug that made the cache
// key silently different from the one the drain derives.
import { base64urlToBytes, bytesToBase64 } from '@/lib/base64url'

const CACHE_KEY   = 'kf49.device.roster'
const CONTACT_KEY = 'kf49.device.last-contact'

/** D7 default. A profile may shorten it; nothing may lengthen it past this. */
export const DEFAULT_OFFLINE_MAX_SECONDS = 24 * 60 * 60

export interface CachedWorker {
  worker_number:   string
  display_name:    string
  photo_path:      string | null
  worker_role:     string
  badge_code_hash: string
  /** Set only once the end date has passed (mig 20261038000000); absent on
   *  rosters cached before it. Drives the leaver line and the sign-in beep. */
  end_date?:       string | null
}

export interface CachedTemplate {
  worker_number:     string
  embedding_version: string
  vector:            number[]
}

/** A pick the tablet may count against, with the units that carry a live rate. */
export interface CachedPick {
  pick_id:     string
  pick_date:   string
  block_label: string
  /**
   * ⚠️ Only unit types with a LIVE PIECE RATE for that block on that date.
   * `device_open_picks()` filters them server-side, because the earnings view
   * prices an unmatched unit_type at R0 — an orchard with no signal is the
   * worst place to discover that, and nobody there can set a rate.
   */
  unit_types:  string[]
}

export interface OfflineRoster {
  tenant_id:  string
  fetched_at: number
  workers:    CachedWorker[]
  templates:  CachedTemplate[]
  /** Phase 4h. Absent on caches written before it — read with `?? []`. */
  picks?:     CachedPick[]
}

// ---------------------------------------------------------------------------
// localStorage, defensively. A device that cannot remember is a device that
// works online only — which is safe. Throwing here would take the lock screen
// down with it.
// ---------------------------------------------------------------------------
function readLocal(key: string): string | null {
  try { return localStorage.getItem(key) } catch { return null }
}
function writeLocal(key: string, value: string): void {
  try { localStorage.setItem(key, value) } catch { /* un-cached is safe */ }
}
function removeLocal(key: string): void {
  try { localStorage.removeItem(key) } catch { /* best effort */ }
}

// ---------------------------------------------------------------------------
// Proven contact — the only honest "are we online" signal.
// ---------------------------------------------------------------------------

/** Call ONLY after a real server response. Never on a `navigator.onLine` read. */
export function stampServerContact(nowMs: number = Date.now()): void {
  writeLocal(CONTACT_KEY, String(nowMs))
}

/** ms since the last PROVEN contact, or null if we have never had one. */
export function msSinceContact(nowMs: number = Date.now()): number | null {
  const raw = Number(readLocal(CONTACT_KEY))
  if (!raw || Number.isNaN(raw)) return null
  // A stamp in the future means the clock moved backwards (NTP correction, a
  // manual change, a tablet that lost its RTC). Treat it as "no idea" rather
  // than as a negative age that would read as freshly contacted.
  if (raw > nowMs) return null
  return nowMs - raw
}

// ---------------------------------------------------------------------------
// Key derivation. HKDF from the device secret, with a distinct info string so
// the cache key can never collide with a queue-signing key derived from the
// same material.
// ---------------------------------------------------------------------------
const INFO = 'kf49.device.roster-cache.v1'

async function cacheKey(): Promise<CryptoKey | null> {
  const bytes = deviceSecretBytes(getDeviceSecret())
  // null = no secret, or a secret that is not the hex we mint. Either way this
  // device gets NO CACHE — never a fallback key, which would encrypt every
  // tenant's roster identically.
  if (!bytes) return null
  const ikm = await crypto.subtle.importKey(
    'raw', bytes.slice().buffer, 'HKDF', false, ['deriveKey'])
  return crypto.subtle.deriveKey(
    { name: 'HKDF', hash: 'SHA-256',
      salt: new Uint8Array(0), info: new TextEncoder().encode(INFO) },
    ikm,
    { name: 'AES-GCM', length: 256 },
    false,                                   // non-extractable
    ['encrypt', 'decrypt'],
  )
}

// ---------------------------------------------------------------------------
// Read / write
// ---------------------------------------------------------------------------

/**
 * Encrypt and store the roster. Silently does nothing when the device has no
 * secret — see the header: no secret means no cache, by design.
 */
export async function saveOfflineRoster(
  roster: OfflineRoster,
): Promise<boolean> {
  const key = await cacheKey()
  if (!key) return false
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const plain = new TextEncoder().encode(JSON.stringify(roster))
  const cipher = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain))
  writeLocal(CACHE_KEY, `${bytesToBase64(iv)}.${bytesToBase64(cipher)}`)
  return true
}

/**
 * The cached roster, or null.
 *
 * ⚠️ null covers every failure — absent, unparseable, undecryptable, EXPIRED.
 * That is deliberate: each of those means "do not trust this to sign anyone
 * in", and a caller that distinguishes them is a caller looking for a reason to
 * proceed anyway.
 *
 * @param offlineMaxSeconds the profile's window; DEFAULT_OFFLINE_MAX_SECONDS
 *        when unknown. Values above the default are CLAMPED — a profile may
 *        shorten the window, never lengthen it past D7.
 */
export async function loadOfflineRoster(
  offlineMaxSeconds: number = DEFAULT_OFFLINE_MAX_SECONDS,
  nowMs: number = Date.now(),
): Promise<OfflineRoster | null> {
  const raw = readLocal(CACHE_KEY)
  if (!raw) return null
  const key = await cacheKey()
  if (!key) return null

  const [ivB64, cipherB64] = raw.split('.')
  if (!ivB64 || !cipherB64) return null

  let roster: OfflineRoster
  try {
    const plain = await crypto.subtle.decrypt(
      // ⚠️ .slice().buffer on BOTH, not just the ciphertext. lib.dom types a
      // Uint8Array as backed by ArrayBufferLike, which could in theory be a
      // SharedArrayBuffer, and WebCrypto's BufferSource rejects that. The iv
      // was missed here and only `tsc` caught it — the tests passed, because at
      // RUNTIME the value is fine. Same reason faceClockHmac slices its key.
      { name: 'AES-GCM', iv: base64urlToBytes(ivB64).slice().buffer }, key,
      base64urlToBytes(cipherB64).slice().buffer)
    roster = JSON.parse(new TextDecoder().decode(plain)) as OfflineRoster
  } catch {
    // Tampered, truncated, or written under a rotated secret. Unreadable is
    // unusable; do not leave it lying about pretending to be a cache.
    removeLocal(CACHE_KEY)
    return null
  }

  const windowSec = Math.min(
    Math.max(0, offlineMaxSeconds || 0), DEFAULT_OFFLINE_MAX_SECONDS)
  const ageMs = nowMs - (roster.fetched_at ?? 0)
  // A negative age is a clock that moved backwards, not a fresh cache.
  if (ageMs < 0 || ageMs > windowSec * 1000) return null

  return roster
}

/** Drop the cache. Called by the WIPE command and by unpairing. */
export function clearOfflineCache(): void {
  removeLocal(CACHE_KEY)
  removeLocal(CONTACT_KEY)
}

// ---------------------------------------------------------------------------
// Offline badge verification
// ---------------------------------------------------------------------------

/**
 * Hash a scanned badge the same way `device_roster()` does in SQL:
 *   sha256(utf8(tenant_id || ':' || badge_code)) → lowercase hex
 *
 * ⚠️ Must stay byte-identical to the SQL. If the two ever disagree, every
 * offline sign-in fails with a message about an unknown badge, on a device with
 * no connection to ask for help — the worst possible place for a mismatch.
 */
export async function hashBadge(tenantId: string, badgeCode: string): Promise<string> {
  const bytes = new TextEncoder().encode(`${tenantId}:${badgeCode}`)
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes))
  let hex = ''
  for (let i = 0; i < digest.length; i++) hex += digest[i].toString(16).padStart(2, '0')
  return hex
}

/**
 * Find the worker a scanned badge belongs to, using the cache alone.
 *
 * Returns null for "not on this roster", which offline is indistinguishable
 * from "revoked since the last refresh" and from "belongs to another tenant".
 * The caller must NOT try to tell those apart — it cannot, and the TTL plus the
 * drain re-verification are what make guessing unnecessary.
 */
export async function findWorkerByBadge(
  roster: OfflineRoster,
  badgeCode: string,
): Promise<CachedWorker | null> {
  const hash = await hashBadge(roster.tenant_id, badgeCode.trim())
  return roster.workers.find((w) => w.badge_code_hash === hash) ?? null
}

/** The cached templates for one worker (a worker may have several). */
export function templatesFor(
  roster: OfflineRoster,
  workerNumber: string,
  embeddingVersion: string,
): number[][] {
  return roster.templates
    .filter((t) => t.worker_number === workerNumber
                && t.embedding_version === embeddingVersion)
    .map((t) => t.vector)
}
